/**
 * Models the workflow of a Diagnosis Review Group Deletes edit form
 */

import Radio from "backbone.radio";
import BaseModel from "./drg-deletes-edit";
import Tooltip from "../../models/tooltip";
import StringKeyValueCollection from "../../collections/key-string-value";
import NoteCollection from "../../collections/notes";
import UserCollection from "../../collections/chart-reviews/reviewers/selectable";
import MemberCategories from "../../models/chart-reviews/member-category";

const channel = Radio.channel("global");

const Model = BaseModel.extend({
  namedAttributes: {
    // indicates whether the DRG can be edited
    _canEdit: Boolean,
    _canVoid: Boolean,
    statusMessage: String,

    auxTypes: StringKeyValueCollection,
    placesOfService: StringKeyValueCollection,
    supplementalReasonCodes: StringKeyValueCollection,
    dispositions: StringKeyValueCollection,
    dispositionLabel: String,
    statusList: StringKeyValueCollection,
    statusLabel: String,
    notesList: NoteCollection,
    actions: StringKeyValueCollection,

    userIsChartReviewAdmin: Boolean,
    canEditClaimCommercialPlanId: Boolean,
    canEditDisposition: Boolean,
    actionEditRules: Object,

    // strongly typed props for proper display in UI
    notes: NoteCollection,
    users: UserCollection,
    statusReasons: Tooltip,
    memberCategories: MemberCategories,
  },

  initialize() {
    // do workflow whenever the model changes
    this.listenTo(this, "change", this._doWorkflow);
    this.listenTo(this, "change:actions", this._onActionsChange);

    // set initial values
    this.set({
      // assume DRG is editable until API says otherwise
      _canEdit: true,
      _canVoid: false,

      userIsChartReviewAdmin: channel.request("user:has-any-role", [
        "ROLE_CHART_REVIEW_ADMIN",
      ]),
      canEditClaimCommercialPlanId: true,
      canEditDisposition: true,
      memberCategories: new MemberCategories(),
    });
  },

  _doWorkflow() {
    // localize props
    const lastSavedStatus = this.get("auditDrgStatus");
    const lastSavedDisposition = this.get("auditDisposition");
    const selectedDisposition = this.get("disposition");

    // set status
    let statusLabel = null;
    const statusList = this.get("statusList");
    if (statusList && lastSavedStatus) {
      const label = statusList.get(lastSavedStatus);
      if (label) {
        statusLabel = label.get("value");
      }
    }

    // define rules
    const userHasValidRole = channel.request("user:has-any-role", [
      "ROLE_ADMIN",
      "ROLE_CHART_REVIEW_ADMIN",
      "ROLE_CHART_REVIEWER",
    ]);

    const canEditClaimCommercialPlanId =
      _.contains(
        ["NOT_READY", "REJECTED", "UNDER_EVALUATION"],
        lastSavedStatus
      ) ||
      ("ACCEPTED" === lastSavedStatus &&
        "V" === lastSavedDisposition &&
        "D" === selectedDisposition);

    const canEditDisposition =
      this.get("canVoid") ||
      _.contains(
        ["NOT_READY", "REJECTED", "UNDER_EVALUATION", "ACCEPTED"],
        lastSavedStatus
      );

    // actions
    const canEditActionMarkInvalid = _.contains(
      ["NOT_READY", "REJECTED", "UNDER_EVALUATION"],
      lastSavedStatus
    );

    const canEditActionMarkValid =
      this._hasStatusReason("UE004") &&
      this.get("userIsChartReviewAdmin") &&
      "UNDER_EVALUATION" === lastSavedStatus;

    const canEditActionApproveSubmission =
      _.contains(["NOT_READY", "REJECTED"], lastSavedStatus) ||
      ("ACCEPTED" === lastSavedStatus &&
        "V" === lastSavedDisposition &&
        "D" === selectedDisposition) ||
      ("D" === lastSavedDisposition && "V" === selectedDisposition);

    this.set({
      _canEdit: this.get("canEdit") && userHasValidRole,
      _canVoid: this.get("canVoid") && userHasValidRole,
      canEditClaimCommercialPlanId,
      canEditDisposition,
      actionEditRules: {
        MARK_INVALID: canEditActionMarkInvalid,
        MARK_VALID: canEditActionMarkValid,
        APPROVE_SUBMISSION: canEditActionApproveSubmission,
      },
      statusLabel,
    });

    this._onActionsChange();

    // run default validation
    this.isValid();

    // set the statue message to the current validation error
    this.set("statusMessage", this.validationError);
  },

  _hasStatusReason(code) {
    const statusReasons = this.get("statusReasons");
    if (statusReasons) {
      const tooltip = statusReasons.get("tooltip");
      if (tooltip) {
        const match = !!tooltip.get(code);
        return match;
      }
    }
    return false;
  },

  // applies workflow generated rules to actions
  _onActionsChange() {
    // grab rules
    const rules = this.get("actionEditRules");

    // grab selections from model
    const data = this.get("actions");
    if (data) {
      // iterate over selections
      data.map((item) => {
        const enabled = rules[item.getKey()] || false;
        item.setEnabled(enabled);
      });

      // save updated selections to model
      this.set("actions", data);
    }
  },
});

export { Model as default };
